import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaStar } from 'react-icons/fa';
import CollapsibleTopPanel from '../../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import {
  getManageBnbBookings,
  findManageBnbBookingById,
  confirmBnbBooking,
  recordBnbBookingPayment,
  cancelBnbBooking,
  rejectBnbBooking,
  confirmUnitRequestPayments,
  assignTenantToUnit,
  assignBnbManual,
  blockBnbUnitDates,
  getBnbUnitBlockedDates,
  normalizeBnbBlockedDatesList,
  unblockBnbBlockedDate,
  getPropertyById,
  getPropertyTenancyHistory,
  searchTenantsByIdentifier,
  getTenantScoreByUserId,
  getTenantScoreByCode,
} from '../../../../api/services/propertyService';
import { fetchAuthenticatedImage } from '../../../../api/services/authService';
import {
  getGuestUserIdFromBooking,
} from '../../../../utils/tenantScoreAccess';
import TenantRatingModal from '../../../shared/TenantRatingModal/TenantRatingModal';
import GuestChatModal from '../../../shared/GuestChatModal/GuestChatModal';
import {
  MdCalendarToday,
  MdAttachMoney,
  MdPerson,
  MdEmail,
  MdInfoOutline,
  MdBusiness,
  MdNightlightRound,
  MdSearch,
  MdClose,
  MdChat,
} from 'react-icons/md';
import '../../../Landlord/components/PropertyUnitRequests/PropertyUnitRequestsView.css';
import { resolveBookingIdFromHint } from '../../../shared/Notifications/notificationUtils';
import './AirbnbBookingsView.css';
import AirbnbCalendar from './AirbnbCalendar';
import BlockDatesModal from './BlockDatesModal';

function formatMoneyKES(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  return `KES ${amount.toLocaleString()}`;
}

function formatDateStr(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(isoString) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'MANUAL', label: 'Manually booked' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'BLOCKED', label: 'Blocked' },
];

const STATUS_FILTER_VALUES = new Set(STATUS_FILTERS.map((f) => f.value));

function parseList(res) {
  const d = res?.data;
  return Array.isArray(d) ? d : [];
}

function getBlockedDateApiId(record) {
  const raw =
    record?.id ||
    record?.blockedDateId ||
    record?.blocked_date_id ||
    null;
  return raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
}

function transformBlockedDateRecord(record, unitMeta) {
  const startDate = record?.startDate || record?.start_date || '';
  const endDate = record?.endDate || record?.end_date || '';
  const reason = record?.reason || '';
  const blockedDateId = getBlockedDateApiId(record);
  const id = blockedDateId || `blocked-${unitMeta.unitId}-${startDate}-${endDate}`;

  return {
    id: String(id),
    blockedDateId,
    status: 'BLOCKED',
    isBlockedDate: true,
    checkInDate: startDate,
    checkOutDate: endDate,
    guestName: reason || 'Blocked',
    reason,
    unitId: unitMeta.unitId,
    unitName: unitMeta.unitName,
    unitNumber: unitMeta.unitName,
    propertyId: unitMeta.propertyId,
    propertyName: unitMeta.propertyName,
    createdAt: record?.createdAt || record?.created_at || null,
  };
}

function matchesBookingSearch(item, query) {
  const q = query.toLowerCase().trim();
  return (
    (item.bookingCode && item.bookingCode.toLowerCase().includes(q)) ||
    (item.guestName && item.guestName.toLowerCase().includes(q)) ||
    (item.reason && item.reason.toLowerCase().includes(q)) ||
    (item.requesterFirstName && item.requesterFirstName.toLowerCase().includes(q)) ||
    (item.requesterLastName && item.requesterLastName.toLowerCase().includes(q)) ||
    (item.guestEmail && item.guestEmail.toLowerCase().includes(q)) ||
    (item.requesterEmail && item.requesterEmail.toLowerCase().includes(q)) ||
    (item.unitName && item.unitName.toLowerCase().includes(q)) ||
    (item.unitNumber && item.unitNumber.toLowerCase().includes(q)) ||
    (item.propertyName && item.propertyName.toLowerCase().includes(q))
  );
}

/**
 * Transforms a Tenancy History item into an Airbnb Booking shape for UI consistency.
 */
const transformTenancyToBooking = (t) => {
  const guestUserId = t.tenantUserId || t.assignedUserId || t.userId || null;
  return {
    ...t,
    guestName: [t.userFirstName, t.userLastName].filter(Boolean).join(' ') || 'Guest',
    guestEmail: t.userEmail,
    guestUserId,
    requesterUserId: guestUserId,
    requesterKycIdDocumentUrl: t.userKycIdDocumentUrl || null,
    requesterKycIdDocumentBackUrl: t.userKycIdDocumentBackUrl || null,
    requesterKycSelfieUrl: t.userKycSelfieUrl || null,
    checkInDate: t.startDate,
    checkOutDate: t.endDate,
    status: 'MANUAL',
  };
};

function isRequestedOrPending(status) {
  const s = String(status || '').toUpperCase();
  return s === 'PENDING' || s === 'REQUESTED';
}

function isApproved(status) {
  return String(status || '').toUpperCase() === 'APPROVED';
}

function isConfirmed(status) {
  return String(status || '').toUpperCase() === 'CONFIRMED';
}

function getGuestKeysForKyc(booking, getGuestUserId) {
  const userIdKey = String(getGuestUserId(booking) || '').trim();
  const emailKey = String(booking?.guestEmail || booking?.requesterEmail || '').trim().toLowerCase();
  const keys = [];
  if (userIdKey) keys.push(`uid:${userIdKey}`);
  if (emailKey) keys.push(`email:${emailKey}`);
  return keys;
}

function buildKycDocsByGuestKey(bookings, resolveKycDocUrls, getGuestUserId) {
  const docsByGuestKey = new Map();
  (Array.isArray(bookings) ? bookings : []).forEach((booking) => {
    const docs = resolveKycDocUrls(booking);
    if (!docs.front && !docs.back && !docs.selfie) return;
    getGuestKeysForKyc(booking, getGuestUserId).forEach((key) => {
      const existing = docsByGuestKey.get(key) || {};
      docsByGuestKey.set(key, {
        front: existing.front || docs.front || null,
        back: existing.back || docs.back || null,
        selfie: existing.selfie || docs.selfie || null,
      });
    });
  });
  return docsByGuestKey;
}

function applyKycGuestFallback(bookings, kycSources, resolveKycDocUrls, getGuestUserId) {
  if (!Array.isArray(bookings) || bookings.length === 0) return [];
  const docsByGuestKey = buildKycDocsByGuestKey(
    [...bookings, ...(Array.isArray(kycSources) ? kycSources : [])],
    resolveKycDocUrls,
    getGuestUserId
  );

  return bookings.map((booking) => {
    const currentDocs = resolveKycDocUrls(booking);
    if (currentDocs.front || currentDocs.back || currentDocs.selfie) return booking;

    const keys = getGuestKeysForKyc(booking, getGuestUserId);
    const fallbackDocs = keys.map((key) => docsByGuestKey.get(key)).find(Boolean) || null;
    if (!fallbackDocs || (!fallbackDocs.front && !fallbackDocs.back && !fallbackDocs.selfie)) {
      return booking;
    }

    return {
      ...booking,
      requesterKycIdDocumentUrl: booking.requesterKycIdDocumentUrl || fallbackDocs.front || null,
      requesterKycIdDocumentBackUrl: booking.requesterKycIdDocumentBackUrl || fallbackDocs.back || null,
      requesterKycSelfieUrl: booking.requesterKycSelfieUrl || fallbackDocs.selfie || null,
    };
  });
}

const AuthenticatedImage = ({ src, alt, className, style, onClick }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!src) return;
      try {
        setLoading(true);
        setError(false);
        const url = await fetchAuthenticatedImage(src);
        if (active) setBlobUrl(url || src);
      } catch (err) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [src]);

  if (!src) return null;

  if (loading) {
    return <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div className="airbnb-bookings-view__spinner" style={{ width: 20, height: 20, borderTopColor: '#0474C4' }} />
    </div>;
  }

  if (error) {
    return <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', color: '#991b1b', fontSize: 11 }}>
      Failed to load image
    </div>;
  }

  return <img src={blobUrl} alt={alt} className={className} style={style} onClick={onClick} />;
};

const AirbnbBookingsView = ({
  properties = [],
  loadingProperties = false,
  pageTitle = 'Bookings',
  emptyPropertiesMessage = 'No properties available.',
  defaultPropertyId = null,
  onNavigate,
  highlightBookingId = null,
  focusBookingOnly = false,
  openPaymentEvidence = false,
  bookingResolveHint = null,
  onFocusExit,
}) => {
  const formatPeriod = (payload) => {
    const fromMonth = payload?.fromMonth;
    const fromYear = payload?.fromYear;
    const toMonth = payload?.toMonth;
    const toYear = payload?.toYear;
    if (!fromMonth || !fromYear || !toMonth || !toYear) return '—';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[fromMonth - 1] || fromMonth} ${fromYear} - ${monthNames[toMonth - 1] || toMonth} ${toYear}`;
  };

  const getScoreBand = (score) => {
    const n = Number(score);
    if (!Number.isFinite(n)) return 'Unknown';
    if (n >= 80) return 'Excellent';
    if (n >= 65) return 'Good';
    if (n >= 50) return 'Fair';
    if (n >= 35) return 'Needs work';
    return 'At risk';
  };
  const [selectedPropertyId, setSelectedPropertyId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [kycSourceBookings, setKycSourceBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionRequestId, setActionRequestId] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');
  const [bookingSearch, setBookingSearch] = useState('');
  const [highlightedBookingId, setHighlightedBookingId] = useState(null);
  const [activeFocusBookingId, setActiveFocusBookingId] = useState(null);
  const focusHandledRef = React.useRef(null);

  useEffect(() => {
    focusHandledRef.current = null;
  }, [highlightBookingId, bookingResolveHint]);

  // Modals state
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectReasonById, setRejectReasonById] = useState({});
  const [approveConfirm, setApproveConfirm] = useState({ open: false, req: null, message: '' });
  const [unblockConfirm, setUnblockConfirm] = useState({
    open: false,
    req: null,
    blockedDateId: '',
    unitLabel: '',
    propertyName: '',
    dateLabel: '',
    reason: '',
  });
  const [confirmingPaymentsRequestId, setConfirmingPaymentsRequestId] = useState(null);
  const [confirmPaymentsById, setConfirmPaymentsById] = useState({});
  const [assignTenantModal, setAssignTenantModal] = useState({ open: false, req: null, userId: '', startDate: '' });
  const [evidenceModalBooking, setEvidenceModalBooking] = useState(null);
  const [evidenceModalError, setEvidenceModalError] = useState('');
  const [chatBooking, setChatBooking] = useState(null);
  const [manualAssignModal, setManualAssignModal] = useState({
    open: false,
    propertyId: '',
    unitId: '',
    userId: '',
    startDate: '',
    endDate: '',
    amountPaid: '',
  });
  const [blockDatesModal, setBlockDatesModal] = useState({
    open: false,
    propertyId: '',
    unitId: '',
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [manualAssignUnitsByProperty, setManualAssignUnitsByProperty] = useState({});
  const [manualAssignLoadingUnits, setManualAssignLoadingUnits] = useState(false);
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [tenantSearchLoading, setTenantSearchLoading] = useState(false);
  const [tenantSearchResults, setTenantSearchResults] = useState([]);
  const [tenantSearchError, setTenantSearchError] = useState('');

  const getUnitIdFromBooking = useCallback((booking) => {
    return (
      booking?.unitId || booking?.unitID || booking?.unit?.id || booking?.unit?.unitId || booking?.propertyUnitId || booking?.propertyUnitID || null
    );
  }, []);

  const getPropertyIdFromBooking = useCallback((booking) => {
    return booking?.propertyId || booking?.propertyID || booking?.property?.id || null;
  }, []);

  const openChatModal = useCallback(
    (booking) => {
      const propertyId = getPropertyIdFromBooking(booking);
      const guestUserId = getGuestUserIdFromBooking(booking);
      if (!propertyId || !guestUserId) {
        setError('Cannot open chat: missing guest or property details.');
        return;
      }
      setChatBooking(booking);
    },
    [getPropertyIdFromBooking]
  );

  const resolveKycDocUrls = useCallback((booking) => {
    if (!booking) return { front: null, back: null, selfie: null };
    const requester = booking.requester || booking.requesterUser || booking.user || null;
    const guest = booking.guest || booking.tenant || null;
    return {
      front:
        booking.requesterKycIdDocumentUrl ||
        booking.guestKycIdDocumentUrl ||
        booking.userKycIdDocumentUrl ||
        requester?.kycIdDocumentUrl ||
        requester?.idDocumentUrl ||
        guest?.kycIdDocumentUrl ||
        guest?.idDocumentUrl ||
        null,
      back:
        booking.requesterKycIdDocumentBackUrl ||
        booking.guestKycIdDocumentBackUrl ||
        booking.userKycIdDocumentBackUrl ||
        requester?.kycIdDocumentBackUrl ||
        requester?.idDocumentBackUrl ||
        guest?.kycIdDocumentBackUrl ||
        guest?.idDocumentBackUrl ||
        null,
      selfie:
        booking.requesterKycSelfieUrl ||
        booking.guestKycSelfieUrl ||
        booking.userKycSelfieUrl ||
        requester?.kycSelfieUrl ||
        requester?.selfieUrl ||
        guest?.kycSelfieUrl ||
        guest?.selfieUrl ||
        null,
    };
  }, []);

  const [scoreModal, setScoreModal] = useState({
    open: false,
    req: null,
    score: null,
    loading: false,
    error: '',
    monthsBack: 12,
  });

  const [kycModal, setKycModal] = useState({ open: false, req: null });
  const [kycImageUrls, setKycImageUrls] = useState({});
  const [kycImagesLoading, setKycImagesLoading] = useState(false);

  const [ratingModal, setRatingModal] = useState({
    open: false,
    req: null,
    tenant: null
  });

  const [ratingSuccessMessage, setRatingSuccessMessage] = useState('');

  const openEvidenceModal = useCallback((booking) => {
    setEvidenceModalError('');
    setEvidenceModalBooking(booking);
  }, []);

  const propertyById = useMemo(() => {
    const m = {};
    properties.forEach((p) => {
      if (p?.id) m[p.id] = p.name || 'Property';
    });
    return m;
  }, [properties]);

  const enrichedBookings = useMemo(() => {
    return applyKycGuestFallback(bookings, kycSourceBookings, resolveKycDocUrls, getGuestUserIdFromBooking);
  }, [bookings, kycSourceBookings, resolveKycDocUrls]);

  const filteredBookings = useMemo(() => {
    let list = enrichedBookings;
    if (bookingSearch.trim()) {
      const query = bookingSearch.toLowerCase().trim();
      list = list.filter((b) => matchesBookingSearch(b, query));
    }
    return list;
  }, [enrichedBookings, bookingSearch]);

  const filteredBlockedDates = useMemo(() => {
    let list = blockedDates;
    if (bookingSearch.trim()) {
      const query = bookingSearch.toLowerCase().trim();
      list = list.filter((b) => matchesBookingSearch(b, query));
    }
    return list;
  }, [blockedDates, bookingSearch]);

  const isBlockedFilter = statusFilter === 'BLOCKED';

  const isFocusMode = Boolean(focusBookingOnly && activeFocusBookingId);

  const displayBookings = useMemo(() => {
    const baseList = isBlockedFilter ? filteredBlockedDates : filteredBookings;
    if (!isFocusMode) return baseList;
    return baseList.filter((b) => String(b.id) === String(activeFocusBookingId));
  }, [filteredBookings, filteredBlockedDates, isBlockedFilter, isFocusMode, activeFocusBookingId]);

  const calendarBookings = useMemo(() => {
    if (isFocusMode) return displayBookings;
    if (isBlockedFilter) return filteredBlockedDates;
    const blockedIds = new Set(filteredBlockedDates.map((b) => String(b.id)));
    const regular = filteredBookings.filter((b) => !blockedIds.has(String(b.id)));
    return [...regular, ...filteredBlockedDates];
  }, [
    displayBookings,
    filteredBlockedDates,
    filteredBookings,
    isBlockedFilter,
    isFocusMode,
  ]);

  const exitFocusMode = useCallback(() => {
    setActiveFocusBookingId(null);
    setHighlightedBookingId(null);
    if (typeof onFocusExit === 'function') {
      onFocusExit();
    }
  }, [onFocusExit]);

  const fetchKycSourceBookings = useCallback(async (statusParam) => {
    if (!statusParam) {
      setKycSourceBookings([]);
      return;
    }
    try {
      if (selectedPropertyId && selectedPropertyId !== 'all') {
        const res = await getManageBnbBookings(selectedPropertyId);
        setKycSourceBookings(parseList(res));
        return;
      }
      const results = await Promise.all(
        properties.map((p) => getManageBnbBookings(p.id).then(parseList).catch(() => []))
      );
      setKycSourceBookings(results.flat());
    } catch {
      setKycSourceBookings([]);
    }
  }, [properties, selectedPropertyId]);

  const loadBlockedDates = useCallback(async () => {
    if (!properties.length) {
      setBlockedDates([]);
      return;
    }

    const scopedProperties =
      selectedPropertyId && selectedPropertyId !== 'all'
        ? properties.filter((p) => String(p.id) === String(selectedPropertyId))
        : properties;

    const unitMetas = [];
    for (const property of scopedProperties) {
      let units = property.units || [];
      if (!units.length && property.id) {
        try {
          const res = await getPropertyById(property.id, true, { dashboardScope: 'BNB' });
          const payload = res?.data;
          units = Array.isArray(payload?.units) ? payload.units : [];
        } catch {
          units = [];
        }
      }
      units.forEach((unit, index) => {
        const unitId = unit?.id || unit?.unitId || unit?.propertyUnitId;
        if (!unitId) return;
        unitMetas.push({
          unitId: String(unitId),
          unitName: unit?.unitNumber || unit?.name || unit?.unitName || `Unit ${index + 1}`,
          propertyId: String(property.id),
          propertyName: property.name || 'Property',
        });
      });
    }

    if (!unitMetas.length) {
      setBlockedDates([]);
      return;
    }

    const results = await Promise.all(
      unitMetas.map(async (meta) => {
        try {
          const res = await getBnbUnitBlockedDates(meta.unitId);
          const list = normalizeBnbBlockedDatesList(res);
          return list.map((item) => transformBlockedDateRecord(item, meta));
        } catch {
          return [];
        }
      })
    );

    const merged = results.flat();
    merged.sort((a, b) => new Date(b.checkInDate || 0) - new Date(a.checkInDate || 0));
    setBlockedDates(merged);
  }, [properties, selectedPropertyId]);

  const loadBookings = useCallback(async () => {
    if (!properties.length) {
      setBookings([]);
      setBlockedDates([]);
      setKycSourceBookings([]);
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await loadBlockedDates();

      if (statusFilter === 'BLOCKED') {
        setBookings([]);
        setKycSourceBookings([]);
        return;
      }

      if (statusFilter === 'MANUAL') {
        setKycSourceBookings([]);
        if (selectedPropertyId && selectedPropertyId !== 'all') {
          const res = await getPropertyTenancyHistory(selectedPropertyId);
          setBookings(parseList(res).map(transformTenancyToBooking));
          return;
        }
        const results = await Promise.all(
          properties.map((p) => getPropertyTenancyHistory(p.id).then(parseList).catch(() => []))
        );
        const merged = results.flat().map(transformTenancyToBooking);
        merged.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setBookings(merged);
        return;
      }

      const statusParam = statusFilter === 'all' ? undefined : statusFilter;

      if (selectedPropertyId && selectedPropertyId !== 'all') {
        const [res] = await Promise.all([
          getManageBnbBookings(selectedPropertyId, statusParam),
          fetchKycSourceBookings(statusParam),
        ]);
        let bookingsList = parseList(res);

        // Fetch manual assignments if we are in 'all' view
        if (statusFilter === 'all') {
          const historyRes = await getPropertyTenancyHistory(selectedPropertyId).then(parseList).catch(() => []);
          const manualRecords = historyRes.map(transformTenancyToBooking);

          const seenIds = new Set(bookingsList.map(b => b.id));
          const seenUnits = new Set(bookingsList.filter(b => b.status === 'ASSIGNED' || b.status === 'CHECKED_IN').map(b => b.unitId));

          manualRecords.forEach(m => {
            if (!seenIds.has(m.id) && !seenUnits.has(m.unitId)) {
              bookingsList.push(m);
            }
          });
        }

        bookingsList.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setBookings(bookingsList);
        return;
      }

      const [results] = await Promise.all([
        Promise.all(
          properties.map((p) =>
            getManageBnbBookings(p.id, statusParam).then(parseList).catch(() => [])
          )
        ),
        fetchKycSourceBookings(statusParam),
      ]);

      let manualRecords = [];
      if (statusFilter === 'all') {
        const historyResults = await Promise.all(
          properties.map((p) => getPropertyTenancyHistory(p.id).then(parseList).catch(() => []))
        );
        manualRecords = historyResults.flat().map(transformTenancyToBooking);
      }

      const merged = [];
      const seenIds = new Set();
      const seenUnits = new Set();

      results.flat().forEach((r) => {
        if (r?.id && !seenIds.has(r.id)) {
          seenIds.add(r.id);
          merged.push(r);
          if ((r.status === 'ASSIGNED' || r.status === 'CHECKED_IN') && r.unitId) {
            seenUnits.add(r.unitId);
          }
        }
      });

      manualRecords.forEach((m) => {
        if (!seenIds.has(m.id) && !seenUnits.has(m.unitId)) {
          seenIds.add(m.id);
          merged.push(m);
        }
      });

      merged.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setBookings(merged);
    } catch (e) {
      setError(e?.message || 'Failed to load bookings.');
      setBookings([]);
      setKycSourceBookings([]);
    } finally {
      setLoading(false);
    }
  }, [properties, selectedPropertyId, statusFilter, fetchKycSourceBookings, loadBlockedDates]);

  /* ─── Accept / Approve ──────────────────────────── */
  const handleApprove = useCallback((booking) => {
    if (!booking?.id) return;
    const guestName = booking.guestName || 'this guest';
    const unitName = booking.unitName || booking.unitNumber || 'this unit';
    setApproveConfirm({
      open: true,
      req: booking,
      message: `Approve booking for ${guestName} at ${unitName}?`,
    });
  }, []);

  const cancelApproveConfirm = useCallback(() => {
    setApproveConfirm({ open: false, req: null, message: '' });
  }, []);

  const confirmApprove = useCallback(async () => {
    const booking = approveConfirm?.req;
    if (!booking?.id) return;

    setActionRequestId(booking.id);
    setError('');
    setSuccessMessage('');
    try {
      const amount = booking.totalAmount || 0;
      await recordBnbBookingPayment(booking.id, amount);
      const res = await confirmBnbBooking(booking.id, amount);
      setSuccessMessage(res?.message || 'Booking approved successfully.');
      cancelApproveConfirm();
      await loadBookings();
    } catch (e) {
      setError(e?.message || 'Failed to approve booking.');
    } finally {
      setActionRequestId(null);
    }
  }, [approveConfirm?.req, cancelApproveConfirm, loadBookings]);

  /* ─── Reject ────────────────────────────────────── */
  const openRejectPanel = useCallback((booking) => {
    if (!booking?.id) return;
    setRejectingRequestId(booking.id);
    setError('');
    setSuccessMessage('');
    setRejectReasonById((prev) => (prev[booking.id] == null ? { ...prev, [booking.id]: '' } : prev));
  }, []);

  const handleEvidenceReject = useCallback(() => {
    const booking = evidenceModalBooking;
    if (!booking?.id) return;
    setEvidenceModalError('');
    setEvidenceModalBooking(null);
    openRejectPanel(booking);
  }, [evidenceModalBooking, openRejectPanel]);

  const cancelReject = useCallback(() => {
    setRejectingRequestId(null);
  }, []);

  const confirmReject = useCallback(
    async (booking) => {
      if (!booking?.id) return;
      const reason = rejectReasonById?.[booking.id] || '';

      setActionRequestId(booking.id);
      setError('');
      setSuccessMessage('');
      try {
        await rejectBnbBooking(booking.id, { reason });
        setSuccessMessage('Booking rejected.');
        setRejectingRequestId(null);
        await loadBookings();
      } catch (e) {
        setError(e?.message || 'Failed to reject booking.');
      } finally {
        setActionRequestId(null);
      }
    },
    [loadBookings, rejectReasonById]
  );

  /* ─── Cancel (simple) ──────────────────────────── */
  const handleCancel = useCallback(
    async (bookingId) => {
      if (!window.confirm('Are you sure you want to cancel this booking?')) return;
      setActionRequestId(bookingId);
      setError('');
      setSuccessMessage('');
      try {
        await cancelBnbBooking(bookingId);
        setSuccessMessage('Booking cancelled.');
        await loadBookings();
      } catch (e) {
        setError(e?.message || 'Failed to cancel booking.');
      } finally {
        setActionRequestId(null);
      }
    },
    [loadBookings]
  );

  const handleViewScore = useCallback(
    async (req, months = 12) => {
      const tenantUserId = getGuestUserIdFromBooking(req);
      const tenantCode = req?.guestTenantCode || req?.requesterTenantCode || req?.tenantCode;

      if (!tenantUserId && !tenantCode) {
        setError('Cannot view score: missing identifier on this booking.');
        return;
      }

      setScoreModal({
        open: true,
        req,
        score: null,
        loading: true,
        error: '',
        monthsBack: months,
      });

      try {
        let res;
        if (tenantCode) {
          res = await getTenantScoreByCode(tenantCode, months);
        } else {
          res = await getTenantScoreByUserId(tenantUserId, months);
        }
        
        setScoreModal((prev) => ({
          ...prev,
          score: res?.data || res,
          loading: false,
        }));
      } catch (e) {
        console.error('View score error:', e);
        setScoreModal((prev) => ({
          ...prev,
          loading: false,
          error: e?.message || 'Failed to load tenant score.',
        }));
      }
    },
    []
  );

  const closeScoreModal = useCallback(() => {
    setScoreModal((prev) => ({ ...prev, open: false, req: null, score: null, loading: false }));
  }, []);

  const openRatingModal = useCallback((booking) => {
    if (!booking) return;
    const tenantId = getGuestUserIdFromBooking(booking);
    if (!tenantId) {
      setError('Cannot rate: missing guest user ID.');
      return;
    }

    const initialTenant = {
      tenantId,
      tenantName: booking.guestName || [booking.requesterFirstName, booking.requesterLastName].filter(Boolean).join(' ') || 'Guest',
      tenantEmail: booking.guestEmail || booking.requesterEmail || ''
    };

    setRatingModal({
      open: true,
      req: booking,
      tenant: initialTenant
    });
  }, []);

  const openKycModal = useCallback(
    async (req) => {
      setKycModal({ open: true, req });
      const reqId = req.id;
      setKycImageUrls((prev) => {
        if (prev[reqId]) return prev;
        return prev;
      });
      if (kycImageUrls[reqId]) return;
      setKycImagesLoading(true);
      try {
        const { front: frontUrl, back: backUrl, selfie: selfieUrl } = resolveKycDocUrls(req);
        const [front, back, selfie] = await Promise.all([
          frontUrl
            ? fetchAuthenticatedImage(frontUrl).catch(() => frontUrl)
            : Promise.resolve(null),
          backUrl
            ? fetchAuthenticatedImage(backUrl).catch(() => backUrl)
            : Promise.resolve(null),
          selfieUrl
            ? fetchAuthenticatedImage(selfieUrl).catch(() => selfieUrl)
            : Promise.resolve(null),
        ]);
        setKycImageUrls((prev) => ({ ...prev, [reqId]: { front, back, selfie } }));
      } catch {
        const { front, back, selfie } = resolveKycDocUrls(req);
        setKycImageUrls((prev) => ({
          ...prev,
          [reqId]: {
            front,
            back,
            selfie,
          },
        }));
      } finally {
        setKycImagesLoading(false);
      }
    },
    [kycImageUrls, resolveKycDocUrls]
  );

  const closeKycModal = useCallback(() => {
    setKycModal({ open: false, req: null });
  }, []);

  /* ─── Open protected file (evidence photos) ───── */
  const openProtectedFile = useCallback(async (e, url, title = 'Preview') => {
    e.preventDefault();
    e.stopPropagation();
    if (!url) return;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      try {
        newWindow.document.title = title;
        newWindow.document.body.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        newWindow.document.body.style.padding = '16px';
        newWindow.document.body.textContent = `Loading ${title.toLowerCase()}…`;
      } catch {
        /* ignore */
      }
    }

    try {
      const blobUrl = await fetchAuthenticatedImage(url);
      const targetUrl = blobUrl || url;
      if (newWindow) {
        try {
          newWindow.opener = null;
        } catch {
          /* ignore */
        }
        newWindow.location.href = targetUrl;
      } else {
        window.open(targetUrl, '_blank');
      }
    } catch (err) {
      if (newWindow) newWindow.close();
      setError(err?.message || `Failed to open ${title.toLowerCase()}.`);
    }
  }, []);

  /* ─── Confirm payments modal ───────────────────── */
  const openConfirmPayments = useCallback((booking) => {
    if (!booking?.id) return;
    setConfirmingPaymentsRequestId(booking.id);
    setError('');
    setSuccessMessage('');
    setConfirmPaymentsById((prev) => {
      if (prev[booking.id]) return prev;
      const hasDepositEvidence = Boolean(booking.depositEvidenceUrl || booking.depositEvidenceText || booking.depositPaidAmount);
      const hasFirstEvidence = Boolean(booking.firstMonthEvidenceUrl || booking.firstMonthEvidenceText || booking.firstMonthPaidAmount);
      return {
        ...prev,
        [booking.id]: {
          depositPaid: hasDepositEvidence ? true : Boolean(booking.depositConfirmed),
          firstMonthPaid: hasFirstEvidence ? true : Boolean(booking.firstMonthConfirmed),
        },
      };
    });
  }, []);

  const cancelConfirmPayments = useCallback(() => {
    setConfirmingPaymentsRequestId(null);
  }, []);

  const submitConfirmPayments = useCallback(
    async (booking) => {
      if (!booking?.propertyId || !booking?.id) return;
      const payload = confirmPaymentsById?.[booking.id];
      if (!payload) return;

      setActionRequestId(booking.id);
      setError('');
      setSuccessMessage('');
      try {
        const res = await confirmUnitRequestPayments(booking.propertyId, booking.id, payload);
        setSuccessMessage(res?.message || 'Payments confirmed.');
        setConfirmingPaymentsRequestId(null);
        await loadBookings();
      } catch (e) {
        setError(e?.message || 'Failed to confirm payments.');
      } finally {
        setActionRequestId(null);
      }
    },
    [confirmPaymentsById, loadBookings]
  );

  const confirmingPaymentsReq = useMemo(() => {
    if (!confirmingPaymentsRequestId) return null;
    return bookings.find((r) => r?.id === confirmingPaymentsRequestId) || null;
  }, [confirmingPaymentsRequestId, bookings]);

  const rejectingReq = useMemo(() => {
    if (!rejectingRequestId) return null;
    return bookings.find((r) => r?.id === rejectingRequestId) || null;
  }, [rejectingRequestId, bookings]);

  /* ─── Assign tenant modal ──────────────────────── */

  const openAssignTenant = useCallback(
    (booking) => {
      if (!booking?.propertyId) return;
      if (booking?.assignedAt || String(booking?.status || '').toUpperCase() === 'ASSIGNED') {
        setError('This unit already has an active tenant assignment.');
        return;
      }
      const unitId = getUnitIdFromBooking(booking);
      if (!unitId) {
        setError('Cannot assign tenant: missing unitId on this booking.');
        return;
      }
      const prefillUserId = getGuestUserIdFromBooking(booking) || '';
      if (!prefillUserId) {
        setError('Cannot assign tenant: missing guest userId on this booking.');
        return;
      }
      setError('');
      setSuccessMessage('');
      setAssignTenantModal({ open: true, req: booking, userId: prefillUserId, startDate: '' });
    },
    [getUnitIdFromBooking]
  );

  const closeAssignTenant = useCallback(() => {
    setAssignTenantModal({ open: false, req: null, userId: '', startDate: '' });
  }, []);

  const normalizeUnitOptions = useCallback((propertyPayload) => {
    const unitsRaw = Array.isArray(propertyPayload?.units)
      ? propertyPayload.units
      : Array.isArray(propertyPayload?.data?.units)
        ? propertyPayload.data.units
        : Array.isArray(propertyPayload?.data)
          ? propertyPayload.data
          : [];
    return unitsRaw
      .map((unit, index) => {
        const id = unit?.id || unit?.unitId || unit?.propertyUnitId || null;
        if (!id) return null;
        const name = unit?.unitNumber || unit?.name || unit?.unitName || `Unit ${index + 1}`;
        const status = String(unit?.status || '').toUpperCase();
        return { id: String(id), name, status };
      })
      .filter(Boolean);
  }, []);

  const openManualAssignModal = useCallback(() => {
    const initialPropertyId =
      selectedPropertyId && selectedPropertyId !== 'all'
        ? selectedPropertyId
        : (properties[0]?.id || '');
    if (!initialPropertyId) {
      setError('No property available for manual assignment.');
      return;
    }
    setError('');
    setSuccessMessage('');
    setManualAssignModal({
      open: true,
      propertyId: String(initialPropertyId),
      unitId: '',
      userId: '',
      startDate: '',
      endDate: '',
      amountPaid: '',
    });
    setTenantSearchQuery('');
    setTenantSearchResults([]);
    setTenantSearchError('');
  }, [properties, selectedPropertyId]);

  const closeManualAssignModal = useCallback(() => {
    setManualAssignModal({
      open: false,
      propertyId: '',
      unitId: '',
      userId: '',
      startDate: '',
      endDate: '',
      amountPaid: '',
    });
    setTenantSearchQuery('');
    setTenantSearchResults([]);
    setTenantSearchError('');
  }, []);

  const openBlockDatesModal = useCallback(() => {
    const initialPropertyId =
      selectedPropertyId && selectedPropertyId !== 'all'
        ? selectedPropertyId
        : (properties[0]?.id || '');
    if (!initialPropertyId) {
      setError('No property available to block dates.');
      return;
    }
    setError('');
    setSuccessMessage('');
    setBlockDatesModal({
      open: true,
      propertyId: String(initialPropertyId),
      unitId: '',
      startDate: '',
      endDate: '',
      reason: '',
    });
  }, [properties, selectedPropertyId]);

  const closeBlockDatesModal = useCallback(() => {
    setBlockDatesModal({
      open: false,
      propertyId: '',
      unitId: '',
      startDate: '',
      endDate: '',
      reason: '',
    });
  }, []);

  useEffect(() => {
    if (!manualAssignModal.open) return;
    const query = String(tenantSearchQuery || '').trim();
    if (manualAssignModal.userId) {
      setTenantSearchError('');
      return;
    }
    if (!query) {
      setTenantSearchResults([]);
      setTenantSearchError('');
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setTenantSearchLoading(true);
      setTenantSearchError('');
      try {
        const res = await searchTenantsByIdentifier(query);
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setTenantSearchResults(rows);
        }
      } catch (e) {
        if (!cancelled) {
          setTenantSearchResults([]);
          setTenantSearchError(e?.message || 'Failed to search tenants.');
        }
      } finally {
        if (!cancelled) {
          setTenantSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [manualAssignModal.open, manualAssignModal.userId, tenantSearchQuery]);

  const activeUnitsModalPropertyId = useMemo(() => {
    if (manualAssignModal.open && manualAssignModal.propertyId) {
      return String(manualAssignModal.propertyId);
    }
    if (blockDatesModal.open && blockDatesModal.propertyId) {
      return String(blockDatesModal.propertyId);
    }
    return '';
  }, [
    manualAssignModal.open,
    manualAssignModal.propertyId,
    blockDatesModal.open,
    blockDatesModal.propertyId,
  ]);

  useEffect(() => {
    if (!activeUnitsModalPropertyId) return;
    const propertyId = activeUnitsModalPropertyId;
    if (Array.isArray(manualAssignUnitsByProperty[propertyId])) return;

    let cancelled = false;
    const loadUnits = async () => {
      setManualAssignLoadingUnits(true);
      try {
        const res = await getPropertyById(propertyId, true, { dashboardScope: 'BNB' });
        const normalized = normalizeUnitOptions(res);
        if (!cancelled) {
          setManualAssignUnitsByProperty((prev) => ({ ...prev, [propertyId]: normalized }));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load units for selected property.');
          setManualAssignUnitsByProperty((prev) => ({ ...prev, [propertyId]: [] }));
        }
      } finally {
        if (!cancelled) setManualAssignLoadingUnits(false);
      }
    };

    loadUnits();
    return () => {
      cancelled = true;
    };
  }, [activeUnitsModalPropertyId, manualAssignUnitsByProperty, normalizeUnitOptions]);

  const submitBlockDates = useCallback(async () => {
    const unitId = String(blockDatesModal.unitId || '').trim();
    const startDate = String(blockDatesModal.startDate || '').trim();
    const endDate = String(blockDatesModal.endDate || '').trim();
    const reason = String(blockDatesModal.reason || '').trim();

    if (!unitId) {
      setError('Select a unit first.');
      return;
    }
    if (!startDate) {
      setError('Start date is required.');
      return;
    }
    if (!endDate) {
      setError('End date is required.');
      return;
    }
    if (endDate < startDate) {
      setError('End date must be on or after the start date.');
      return;
    }

    const requestId = 'block-bnb-dates';
    setActionRequestId(requestId);
    setError('');
    setSuccessMessage('');
    try {
      const res = await blockBnbUnitDates(unitId, { startDate, endDate, reason });
      setSuccessMessage(res?.message || 'Dates blocked successfully. Guests cannot book this range.');
      closeBlockDatesModal();
      await loadBlockedDates();
      if (statusFilter !== 'BLOCKED') {
        await loadBookings();
      }
    } catch (e) {
      setError(e?.message || 'Failed to block dates.');
    } finally {
      setActionRequestId(null);
    }
  }, [
    blockDatesModal.endDate,
    blockDatesModal.reason,
    blockDatesModal.startDate,
    blockDatesModal.unitId,
    closeBlockDatesModal,
    loadBlockedDates,
    loadBookings,
    statusFilter,
  ]);

  const openUnblockConfirm = useCallback((blockedItem) => {
    const blockedDateId =
      blockedItem?.blockedDateId ||
      (blockedItem?.id && !String(blockedItem.id).startsWith('blocked-')
        ? blockedItem.id
        : null);

    if (!blockedDateId) {
      setError('Cannot unblock: missing blocked date id from server.');
      return;
    }

    setError('');
    setUnblockConfirm({
      open: true,
      req: blockedItem,
      blockedDateId,
      unitLabel: blockedItem.unitName || blockedItem.unitNumber || 'Unit',
      propertyName:
        blockedItem.propertyName || propertyById[blockedItem.propertyId] || 'Property',
      dateLabel: `${formatDateStr(blockedItem.checkInDate)} – ${formatDateStr(blockedItem.checkOutDate)}`,
      reason: blockedItem.reason || '',
    });
  }, [propertyById]);

  const cancelUnblockConfirm = useCallback(() => {
    setUnblockConfirm({
      open: false,
      req: null,
      blockedDateId: '',
      unitLabel: '',
      propertyName: '',
      dateLabel: '',
      reason: '',
    });
  }, []);

  const confirmUnblock = useCallback(async () => {
    const blockedDateId = unblockConfirm.blockedDateId;
    if (!blockedDateId) return;

    const requestId = `unblock-${blockedDateId}`;
    setActionRequestId(requestId);
    setError('');
    setSuccessMessage('');
    try {
      const res = await unblockBnbBlockedDate(blockedDateId);
      setSuccessMessage(res?.message || 'Dates unblocked successfully.');
      cancelUnblockConfirm();
      await loadBlockedDates();
      if (statusFilter !== 'BLOCKED') {
        await loadBookings();
      }
    } catch (e) {
      setError(e?.message || 'Failed to unblock dates.');
    } finally {
      setActionRequestId(null);
    }
  }, [
    unblockConfirm.blockedDateId,
    cancelUnblockConfirm,
    loadBlockedDates,
    loadBookings,
    statusFilter,
  ]);

  const submitManualAssign = useCallback(async () => {
    const propertyId = String(manualAssignModal.propertyId || '').trim();
    const unitId = String(manualAssignModal.unitId || '').trim();
    const userId = String(manualAssignModal.userId || '').trim();
    const startDate = String(manualAssignModal.startDate || '').trim();
    const amountPaid = manualAssignModal.amountPaid ? Number(manualAssignModal.amountPaid) : 0;

    if (!propertyId) {
      setError('Select a property first.');
      return;
    }
    if (!unitId) {
      setError('Select a unit first.');
      return;
    }
    if (!userId) {
      setError('Tenant user ID is required.');
      return;
    }

    const requestId = 'manual-bnb-assign';
    setActionRequestId(requestId);
    setError('');
    setSuccessMessage('');
    try {
      if (!startDate) {
        setError('Start date (check-in) is required for BnB bookings.');
        return;
      }
      if (!manualAssignModal.endDate) {
        setError('End date (check-out) is required for BnB bookings.');
        return;
      }

      const payload = {
        userId,
        startDate,
        endDate: manualAssignModal.endDate,
        amountPaid,
        agreementAccepted: true,
      };
      const res = await assignBnbManual(propertyId, unitId, payload);
      setSuccessMessage(res?.message || 'Tenant assigned manually.');
      closeManualAssignModal();
      await loadBookings();
    } catch (e) {
      setError(e?.message || 'Failed to assign tenant manually.');
    } finally {
      setActionRequestId(null);
    }
  }, [closeManualAssignModal, loadBookings, manualAssignModal.propertyId, manualAssignModal.startDate, manualAssignModal.endDate, manualAssignModal.amountPaid, manualAssignModal.unitId, manualAssignModal.userId]);

  const submitAssignTenant = useCallback(async () => {
    const booking = assignTenantModal.req;
    if (!booking?.propertyId) return;
    const unitId = getUnitIdFromBooking(booking);
    if (!unitId) {
      setError('Cannot assign tenant: missing unitId on this booking.');
      return;
    }
    const userId = String(assignTenantModal.userId || '').trim();
    if (!userId) {
      setError('Cannot assign tenant: missing guest userId on this booking.');
      return;
    }

    setActionRequestId(booking.id);
    setError('');
    setSuccessMessage('');
    try {
      const payload = {
        userId,
        ...(assignTenantModal.startDate && String(assignTenantModal.startDate).trim()
          ? { startDate: String(assignTenantModal.startDate).trim() }
          : {}),
        agreementAccepted: true,
      };
      const res = await assignTenantToUnit(booking.propertyId, unitId, payload);
      setSuccessMessage(res?.message || 'Guest assigned to unit.');
      closeAssignTenant();
      await loadBookings();
    } catch (e) {
      setError(e?.message || 'Failed to assign guest to unit.');
    } finally {
      setActionRequestId(null);
    }
  }, [assignTenantModal.req, assignTenantModal.startDate, assignTenantModal.userId, closeAssignTenant, getUnitIdFromBooking, loadBookings]);

  /* ─── Escape key handlers  ─────────────────────── */
  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') {
        if (evidenceModalBooking) setEvidenceModalBooking(null);
        if (confirmingPaymentsRequestId) cancelConfirmPayments();
        if (approveConfirm.open) cancelApproveConfirm();
        if (unblockConfirm.open) cancelUnblockConfirm();
        if (assignTenantModal.open) closeAssignTenant();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    evidenceModalBooking,
    cancelConfirmPayments,
    confirmingPaymentsRequestId,
    approveConfirm.open,
    cancelApproveConfirm,
    unblockConfirm.open,
    cancelUnblockConfirm,
    assignTenantModal.open,
    closeAssignTenant,
  ]);

  /* ─── Load on dependency change ─────────────────── */
  useEffect(() => {
    if (!STATUS_FILTER_VALUES.has(statusFilter)) {
      setStatusFilter('all');
    }
  }, [statusFilter]);

  useEffect(() => {
    if (loadingProperties) return;
    loadBookings();
  }, [loadingProperties, loadBookings]);

  useEffect(() => {
    if (loadingProperties || !defaultPropertyId || !properties.length) return;
    const exists = properties.some((p) => p.id === defaultPropertyId);
    if (exists) setSelectedPropertyId(defaultPropertyId);
  }, [loadingProperties, defaultPropertyId, properties]);

  /* ─── Helpers for evidence presence ────────────── */
  const hasPaymentEvidence = (booking) =>
    Boolean(
      booking.depositEvidenceUrl ||
      booking.depositEvidenceText ||
      booking.depositPaidAmount ||
      booking.firstMonthEvidenceUrl ||
      booking.firstMonthEvidenceText ||
      booking.firstMonthPaidAmount ||
      booking.bookingFeeEvidenceText ||
      booking.bookingFeeEvidencePhotoUrl
    );

  const paymentsFullyConfirmed = useCallback((booking) =>
    Boolean(booking.depositConfirmed) && Boolean(booking.firstMonthConfirmed), []);

  const handleBookingClick = useCallback((booking) => {
    if (!booking) return;
    const status = (booking.status || '').toUpperCase();

    // Logic similar to card actions
    if (status === 'PENDING' || status === 'REQUESTED') {
      openEvidenceModal(booking);
    } else if (status === 'APPROVED' || status === 'CONFIRMED') {
      if (paymentsFullyConfirmed(booking)) {
        openAssignTenant(booking);
      } else {
        openEvidenceModal(booking);
      }
    } else {
      openEvidenceModal(booking);
    }
  }, [openEvidenceModal, paymentsFullyConfirmed, openAssignTenant]);

  useEffect(() => {
    if (loadingProperties) return;
    if (!highlightBookingId && !bookingResolveHint && !focusBookingOnly) return;

    let cancelled = false;

    const applyFocusToBooking = (booking, targetId) => {
      if (!booking || cancelled) return;

      const handleKey = `${targetId}:${openPaymentEvidence}`;
      if (focusHandledRef.current === handleKey) return;
      focusHandledRef.current = handleKey;

      setViewMode('list');
      setStatusFilter('all');
      setBookingSearch('');
      if (booking.propertyId) {
        setSelectedPropertyId(booking.propertyId);
      }
      setHighlightedBookingId(targetId);
      if (focusBookingOnly) {
        setActiveFocusBookingId(targetId);
      }

      window.setTimeout(() => {
        if (cancelled) return;
        const el = document.querySelector(`[data-booking-id="${targetId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);

      window.setTimeout(() => {
        if (cancelled) return;
        if (openPaymentEvidence) {
          openEvidenceModal(booking);
        } else {
          handleBookingClick(booking);
        }
      }, 280);
    };

    (async () => {
      setStatusFilter('all');

      let targetId = highlightBookingId ? String(highlightBookingId) : null;
      const idMatches = (b, id) => String(b?.id).toLowerCase() === String(id).toLowerCase();
      let booking = targetId ? bookings.find((b) => idMatches(b, targetId)) : null;

      if (!booking && targetId && properties.length > 0) {
        const fetched = await findManageBnbBookingById(properties, targetId);
        if (fetched && !cancelled) {
          booking = fetched;
          setBookings((prev) => {
            if (prev.some((b) => String(b.id) === targetId)) return prev;
            return [fetched, ...prev];
          });
        }
      }

      if (!booking && !targetId && bookingResolveHint && properties.length > 0) {
        const results = await Promise.all(
          properties.map((p) =>
            getManageBnbBookings(p.id).then(parseList).catch(() => [])
          )
        );
        const allBookings = results.flat();
        targetId = resolveBookingIdFromHint(allBookings, bookingResolveHint);
        if (targetId) {
          booking =
            allBookings.find((b) => idMatches(b, targetId)) ||
            (await findManageBnbBookingById(properties, targetId));
          if (booking && !cancelled) {
            const resolved = booking;
            setBookings((prev) => {
              if (prev.some((b) => String(b.id) === targetId)) return prev;
              return [resolved, ...prev];
            });
          }
        }
      }

      if (!booking || !targetId || cancelled) return;
      applyFocusToBooking(booking, targetId);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    highlightBookingId,
    bookingResolveHint,
    focusBookingOnly,
    openPaymentEvidence,
    bookings,
    loading,
    loadingProperties,
    properties,
    openEvidenceModal,
    handleBookingClick,
  ]);

  const activeKycDocUrls = resolveKycDocUrls(kycModal.req);

  return (
    <div className="airbnb-bookings-view">
      {loadingProperties ? (
        <div className="airbnb-bookings-view__state">
          <div className="airbnb-bookings-view__spinner" aria-hidden />
          <p>Loading properties…</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="airbnb-bookings-view__state airbnb-bookings-view__state--muted">
          <MdBusiness aria-hidden />
          <p>{emptyPropertiesMessage}</p>
        </div>
      ) : (
        <>
          {isFocusMode && (
            <div className="airbnb-bookings-view__focus-bar">
              <button type="button" className="airbnb-bookings-view__focus-back" onClick={exitFocusMode}>
                ← All bookings
              </button>
              <span className="airbnb-bookings-view__focus-label">Booking request</span>
            </div>
          )}

          {!isFocusMode && (
          <CollapsibleTopPanel id="airbnb-bookings-filters" defaultOpenOnMobile={false}>
            <div className="airbnb-bookings-view__header">
              <h1 className="airbnb-bookings-view__title">{pageTitle}</h1>
              <p className="airbnb-bookings-view__subtitle">
                Manage Airbnb bookings for your properties. Review guest details, payment evidence, and approve or reject requests.
              </p>
            </div>
            <div className="airbnb-bookings-view__toolbar">
              <div className="airbnb-bookings-view__field">
                <label htmlFor="ab-property">Property</label>
                <select
                  id="ab-property"
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  disabled={loading}
                >
                  <option value="all">All properties ({properties.length})</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="airbnb-bookings-view__field">
                <label htmlFor="ab-status">Status</label>
                <select
                  id="ab-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={loading}
                >
                  {STATUS_FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="airbnb-bookings-view__field airbnb-bookings-view__field--search">
                <label htmlFor="ab-search">Search</label>
                <div className="airbnb-bookings-view__search-input-wrap">
                  <MdSearch className="airbnb-bookings-view__search-icon" />
                  <input
                    id="ab-search"
                    type="text"
                    placeholder="Code, name, or unit..."
                    value={bookingSearch}
                    onChange={(e) => setBookingSearch(e.target.value)}
                    className="airbnb-bookings-view__search-input"
                  />
                  {bookingSearch && (
                    <button
                      type="button"
                      className="airbnb-bookings-view__search-clear"
                      onClick={() => setBookingSearch('')}
                      title="Clear search"
                    >
                      <MdClose />
                    </button>
                  )}
                </div>
              </div>

              <div className="airbnb-bookings-view__view-toggle">
                <button
                  type="button"
                  className={`airbnb-bookings-view__toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </button>
                <button
                  type="button"
                  className={`airbnb-bookings-view__toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  List
                </button>
              </div>
              <div className="airbnb-bookings-view__toolbar-actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--danger airbnb-bookings-view__toolbar-action"
                  onClick={openBlockDatesModal}
                  disabled={loading}
                >
                  Block dates
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm airbnb-bookings-view__toolbar-action"
                  onClick={openManualAssignModal}
                  disabled={loading}
                >
                  Add Guest manually
                </button>
              </div>
            </div>
          </CollapsibleTopPanel>
          )}

          {error && (
            <div className="airbnb-bookings-view__banner airbnb-bookings-view__banner--error" role="alert">
              {error}
            </div>
          )}
          {successMessage && !error && (
            <div className="airbnb-bookings-view__banner airbnb-bookings-view__banner--success" role="status">
              {successMessage}
            </div>
          )}

          {loading && !error ? (
            <div className="airbnb-bookings-view__state">
              <div className="airbnb-bookings-view__spinner" aria-hidden />
              <p>{isBlockedFilter ? 'Loading blocked dates…' : 'Loading bookings…'}</p>
            </div>
          ) : viewMode === 'calendar' && !isFocusMode ? (
            <AirbnbCalendar
              bookings={calendarBookings}
              properties={properties}
              onAction={handleBookingClick}
              formatMoneyKES={formatMoneyKES}
              formatDateStr={formatDateStr}
              propertyById={propertyById}
            />
          ) : (
            <div className={`airbnb-bookings-view__grid${isFocusMode ? ' airbnb-bookings-view__grid--focus' : ''}`}>
              {displayBookings.length === 0 ? (
                <div className="airbnb-bookings-view__state airbnb-bookings-view__state--muted" style={{ gridColumn: '1 / -1' }}>
                  <MdSearch aria-hidden />
                  <p>
                    {isFocusMode
                      ? 'Could not load this booking. It may have been updated or removed.'
                      : isBlockedFilter
                        ? bookingSearch.trim()
                          ? `No blocked dates found matching "${bookingSearch}"`
                          : 'No blocked dates for the selected property.'
                        : bookingSearch.trim()
                          ? `No bookings found matching "${bookingSearch}"`
                          : 'No bookings found.'}
                  </p>
                  {isFocusMode && (
                    <button type="button" className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost" onClick={exitFocusMode}>
                      View all bookings
                    </button>
                  )}
                </div>
              ) : (
                displayBookings.map((booking) => {
                  const isBlockedItem =
                    booking.isBlockedDate || String(booking.status || '').toUpperCase() === 'BLOCKED';

                  if (isBlockedItem) {
                    return (
                      <article
                        key={booking.id}
                        className="airbnb-bookings-view__card airbnb-bookings-view__card--blocked"
                      >
                        <div className="airbnb-bookings-view__card-top">
                          <div className="airbnb-bookings-view__card-title-block">
                            <h3 className="airbnb-bookings-view__unit">
                              {booking.unitName || booking.unitNumber || 'Unit'}
                            </h3>
                            <span className="airbnb-bookings-view__property-name">
                              {booking.propertyName || propertyById[booking.propertyId] || 'Property'}
                            </span>
                          </div>
                          <span className="airbnb-bookings-view__badge airbnb-bookings-view__badge--blocked">
                            Blocked
                          </span>
                        </div>
                        <div className="airbnb-bookings-view__meta-block">
                          <div className="airbnb-bookings-view__meta-row">
                            <MdCalendarToday aria-hidden />
                            <span>
                              {formatDateStr(booking.checkInDate)} &rarr; {formatDateStr(booking.checkOutDate)}
                            </span>
                          </div>
                          {booking.reason && (
                            <div className="airbnb-bookings-view__meta-row">
                              <MdInfoOutline aria-hidden />
                              <span>{booking.reason}</span>
                            </div>
                          )}
                        </div>
                        {isBlockedFilter && (
                          <div className="airbnb-bookings-view__actions">
                            <button
                              type="button"
                              className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                              onClick={() => openUnblockConfirm(booking)}
                              disabled={
                                loading ||
                                !booking.blockedDateId ||
                                actionRequestId === `unblock-${booking.blockedDateId}`
                              }
                              title={
                                booking.blockedDateId
                                  ? 'Remove this blocked date range'
                                  : 'Blocked date id missing — refresh and try again'
                              }
                            >
                              {actionRequestId === `unblock-${booking.blockedDateId}`
                                ? 'Unblocking…'
                                : 'Unblock dates'}
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  }

                  return (
                    <article
                      key={booking.id}
                      data-booking-id={booking.id}
                      className={`airbnb-bookings-view__card${String(booking.id) === highlightedBookingId ? ' airbnb-bookings-view__card--highlighted' : ''}`}
                    >
                      {/* ── Card header ── */}
                      <div className="airbnb-bookings-view__card-top">
                        <div className="airbnb-bookings-view__card-title-block">
                          <h3 className="airbnb-bookings-view__unit">
                            {booking.unitName || booking.unitNumber || 'Unit'}
                          </h3>
                          <span className="airbnb-bookings-view__property-name">
                            {booking.propertyName || propertyById[booking.propertyId] || 'Property'}
                          </span>
                        </div>
                        <span
                          className={`airbnb-bookings-view__badge airbnb-bookings-view__badge--${(booking.status || '').toLowerCase()}`}
                        >
                          {booking.status || '—'}
                        </span>
                      </div>

                      {/* ── Guest info ── */}
                      <div className="airbnb-bookings-view__guest">
                        <MdPerson aria-hidden />
                        <div className="airbnb-bookings-view__guest-info">
                          <div className="airbnb-bookings-view__guest-name">
                            {booking.guestName ||
                              [booking.requesterFirstName, booking.requesterLastName].filter(Boolean).join(' ') ||
                              'Guest'}
                          </div>
                          <div className="airbnb-bookings-view__guest-email">
                            <MdEmail aria-hidden />
                            {booking.guestEmail || booking.requesterEmail || '—'}
                          </div>
                        </div>
                      </div>

                      {/* ── Meta block ── */}
                      <div className="airbnb-bookings-view__meta-block">
                        {booking.bookingCode && (
                          <div className="airbnb-bookings-view__meta-row airbnb-bookings-view__meta-row--code">
                            <MdInfoOutline aria-hidden />
                            <span>Code: <strong>{booking.bookingCode}</strong></span>
                          </div>
                        )}
                        <div className="airbnb-bookings-view__meta-row">
                          <MdCalendarToday aria-hidden />
                          <span>{formatDateStr(booking.checkInDate)} &rarr; {formatDateStr(booking.checkOutDate)}</span>
                        </div>
                        <div className="airbnb-bookings-view__meta-row">
                          <MdNightlightRound aria-hidden />
                          <span>{booking.nights || 0} Nights</span>
                        </div>
                        <div className="airbnb-bookings-view__meta-row">
                          <MdAttachMoney aria-hidden />
                          <span>{formatMoneyKES(booking.pricePerNight)} / night</span>
                        </div>
                        <div className="airbnb-bookings-view__meta-row">
                          <MdCalendarToday aria-hidden />
                          <span>Requested: {formatDateTime(booking.createdAt)}</span>
                        </div>
                      </div>

                      {/* ── Total amount ── */}
                      <div className="airbnb-bookings-view__money">
                        <span className="airbnb-bookings-view__money-label">Total Amount</span>
                        <span className="airbnb-bookings-view__money-amount">
                          {formatMoneyKES(booking.totalAmount) || '—'}
                        </span>
                      </div>

                      <div className="airbnb-bookings-view__actions airbnb-bookings-view__actions--compact">
                        <button
                          type="button"
                          className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                          onClick={() => openEvidenceModal(booking)}
                        >
                          View info
                        </button>
                        <button
                          type="button"
                          className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost airbnb-bookings-view__button--chat"
                          onClick={() => openChatModal(booking)}
                        >
                          <MdChat aria-hidden />
                          Message
                        </button>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          )}

          {!loading && displayBookings.length > 0 && (
            <p className="airbnb-bookings-view__count">
              {displayBookings.length}{' '}
              {isBlockedFilter
                ? `blocked date range${displayBookings.length !== 1 ? 's' : ''}`
                : `booking${displayBookings.length !== 1 ? 's' : ''}`}
            </p>
          )}
        </>
      )}

      {/* ─── Unblock confirmation modal ───────────── */}
      {unblockConfirm.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="ab-modal-overlay ab-confirm-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unblock-confirm-title"
            onClick={cancelUnblockConfirm}
          >
            <div className="ab-modal ab-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="ab-confirm-modal__close"
                onClick={cancelUnblockConfirm}
                disabled={actionRequestId === `unblock-${unblockConfirm.blockedDateId}`}
                aria-label="Close"
              >
                <MdClose />
              </button>
              <div className="ab-confirm-modal__icon-wrap" aria-hidden>
                <span className="ab-confirm-modal__icon">📅</span>
              </div>
              <h2 id="unblock-confirm-title" className="ab-confirm-modal__title">
                Unblock these dates?
              </h2>
              <p className="ab-confirm-modal__subtitle">
                Guests will be able to book this range again once you confirm.
              </p>
              <dl className="ab-confirm-modal__details">
                <div className="ab-confirm-modal__detail-row">
                  <dt>Property</dt>
                  <dd>{unblockConfirm.propertyName}</dd>
                </div>
                <div className="ab-confirm-modal__detail-row">
                  <dt>Unit</dt>
                  <dd>{unblockConfirm.unitLabel}</dd>
                </div>
                <div className="ab-confirm-modal__detail-row">
                  <dt>Dates</dt>
                  <dd>{unblockConfirm.dateLabel}</dd>
                </div>
                {unblockConfirm.reason && (
                  <div className="ab-confirm-modal__detail-row">
                    <dt>Reason</dt>
                    <dd>{unblockConfirm.reason}</dd>
                  </div>
                )}
              </dl>
              <div className="ab-modal__actions ab-confirm-modal__actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={cancelUnblockConfirm}
                  disabled={actionRequestId === `unblock-${unblockConfirm.blockedDateId}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                  onClick={confirmUnblock}
                  disabled={loading || actionRequestId === `unblock-${unblockConfirm.blockedDateId}`}
                >
                  {actionRequestId === `unblock-${unblockConfirm.blockedDateId}`
                    ? 'Unblocking…'
                    : 'Yes, unblock dates'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ════════════════════════════════════════════ */}
      {/* ─── Approve confirmation modal ───────────── */}
      {/* ════════════════════════════════════════════ */}
      {approveConfirm.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="ab-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm approval"
            onClick={cancelApproveConfirm}
          >
            <div className="ab-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__title">Approve booking</div>
              <div className="ab-modal__message">{approveConfirm.message}</div>
              <div className="ab-modal__actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={cancelApproveConfirm}
                  disabled={actionRequestId === approveConfirm?.req?.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                  onClick={confirmApprove}
                  disabled={loading || actionRequestId === approveConfirm?.req?.id}
                >
                  {actionRequestId === approveConfirm?.req?.id ? 'Working…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ════════════════════════════════════════════ */}
      {/* ─── Confirm payments modal ───────────────── */}
      {/* ════════════════════════════════════════════ */}
      {confirmingPaymentsReq &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="ab-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm payments"
            onClick={cancelConfirmPayments}
          >
            <div className="ab-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__title">Confirm payments</div>
              <div className="ab-modal__message">Review the evidence, then tick what's paid and confirm.</div>

              <div className="ab-payments ab-payments--single">
                <div className="ab-payments__col ab-payments__col--full">
                  <div className="ab-payments__label">Booking Payment Details</div>
                  {confirmingPaymentsReq.bookingCode && (
                    <div className="airbnb-bookings-view__meta-row airbnb-bookings-view__meta-row--code" style={{ marginBottom: 12 }}>
                      <MdInfoOutline aria-hidden />
                      <span>Code: <strong>{confirmingPaymentsReq.bookingCode}</strong></span>
                    </div>
                  )}
                  <div className="ab-payments__value">
                    {formatMoneyKES(confirmingPaymentsReq.depositPaidAmount || confirmingPaymentsReq.firstMonthPaidAmount) || '—'}
                  </div>
                  {(confirmingPaymentsReq.depositEvidenceText || confirmingPaymentsReq.firstMonthEvidenceText) && (
                    <div className="ab-payments__note">
                      {confirmingPaymentsReq.depositEvidenceText || confirmingPaymentsReq.firstMonthEvidenceText}
                    </div>
                  )}
                  {(confirmingPaymentsReq.depositEvidenceUrl || confirmingPaymentsReq.firstMonthEvidenceUrl) && (
                    <div style={{ marginBottom: 12 }}>
                      <div className="airbnb-bookings-view__image-preview" onClick={(e) => openProtectedFile(e, confirmingPaymentsReq.depositEvidenceUrl || confirmingPaymentsReq.firstMonthEvidenceUrl, 'Photo')}>
                        <AuthenticatedImage src={confirmingPaymentsReq.depositEvidenceUrl || confirmingPaymentsReq.firstMonthEvidenceUrl} alt="Receipt Preview" />
                      </div>
                      <a
                        className="airbnb-bookings-view__payments-link"
                        href={confirmingPaymentsReq.depositEvidenceUrl || confirmingPaymentsReq.firstMonthEvidenceUrl}
                        onClick={(e) => openProtectedFile(e, confirmingPaymentsReq.depositEvidenceUrl || confirmingPaymentsReq.firstMonthEvidenceUrl, 'Photo')}
                      >
                        View photo
                      </a>
                    </div>
                  )}
                  <label className="ab-check">
                    <input
                      type="checkbox"
                      checked={Boolean(confirmPaymentsById?.[confirmingPaymentsReq.id]?.depositPaid)}
                      onChange={(e) =>
                        setConfirmPaymentsById((prev) => ({
                          ...prev,
                          [confirmingPaymentsReq.id]: {
                            ...(prev[confirmingPaymentsReq.id] || {}),
                            depositPaid: e.target.checked,
                            // Ensure both are toggled for simplicity in the unified flow
                            firstMonthPaid: e.target.checked,
                          },
                        }))
                      }
                    />
                    Booking payment confirmed
                  </label>
                </div>
              </div>

              <div className="ab-modal__actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={cancelConfirmPayments}
                  disabled={loading || actionRequestId === confirmingPaymentsReq.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                  onClick={() => submitConfirmPayments(confirmingPaymentsReq)}
                  disabled={loading || actionRequestId === confirmingPaymentsReq.id}
                >
                  {actionRequestId === confirmingPaymentsReq.id ? 'Working…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ════════════════════════════════════════════ */}
      {/* ─── Assign tenant modal ──────────────────── */}
      {/* ════════════════════════════════════════════ */}
      {assignTenantModal.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="ab-modal-overlay" role="dialog" aria-modal="true" aria-label="Assign guest" onClick={closeAssignTenant}>
            <div className="ab-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__title">Assign guest to unit</div>
              <div className="ab-modal__message">
                Select the start date (optional). The guest is picked automatically from the booking.
              </div>

              <div className="ab-form">
                <label className="ab-form__label">
                  Start date (optional)
                  <input
                    className="ab-form__input"
                    type="date"
                    value={assignTenantModal.startDate}
                    onChange={(e) => setAssignTenantModal((prev) => ({ ...prev, startDate: e.target.value }))}
                    disabled={loading || actionRequestId === assignTenantModal?.req?.id}
                  />
                </label>

                <div className="ab-form__hint">
                  This will assign <strong>{assignTenantModal?.req?.guestEmail || assignTenantModal?.req?.guestName || 'the guest'}</strong> to{' '}
                  <strong>{assignTenantModal?.req?.unitName || assignTenantModal?.req?.unitNumber || 'the unit'}</strong>.
                </div>
              </div>

              <div className="ab-modal__actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={closeAssignTenant}
                  disabled={loading || actionRequestId === assignTenantModal?.req?.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                  onClick={submitAssignTenant}
                  disabled={loading || actionRequestId === assignTenantModal?.req?.id}
                >
                  {actionRequestId === assignTenantModal?.req?.id ? 'Working…' : 'Assign guest'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <BlockDatesModal
        open={blockDatesModal.open}
        onClose={closeBlockDatesModal}
        properties={properties}
        units={manualAssignUnitsByProperty[blockDatesModal.propertyId] || []}
        loadingUnits={manualAssignLoadingUnits}
        form={blockDatesModal}
        onFormChange={setBlockDatesModal}
        onSubmit={submitBlockDates}
        submitting={actionRequestId === 'block-bnb-dates'}
        error={blockDatesModal.open && error ? error : ''}
      />

      {manualAssignModal.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="ab-modal-overlay" role="dialog" aria-modal="true" aria-label="Add tenant manually" onClick={closeManualAssignModal}>
            <div className="ab-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__title">Add Guest manually</div>
              <div className="ab-modal__message">
                Assign a guest directly from the bookings page using tenant user ID.
              </div>
              {error && (
                <div className="airbnb-bookings-view__banner airbnb-bookings-view__banner--error" role="alert" style={{ marginTop: 12 }}>
                  {error}
                </div>
              )}

              <div className="ab-form">
                <label className="ab-form__label">
                  Property
                  <select
                    className="ab-form__input"
                    value={manualAssignModal.propertyId}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, propertyId: e.target.value, unitId: '' }))}
                    disabled={loading || actionRequestId === 'manual-bnb-assign'}
                  >
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.id}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="ab-form__label">
                  Unit
                  <select
                    className="ab-form__input"
                    value={manualAssignModal.unitId}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, unitId: e.target.value }))}
                    disabled={loading || manualAssignLoadingUnits || actionRequestId === 'manual-bnb-assign'}
                  >
                    <option value="">{manualAssignLoadingUnits ? 'Loading units…' : 'Select unit'}</option>
                    {(manualAssignUnitsByProperty[manualAssignModal.propertyId] || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}{u.status ? ` (${u.status})` : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="ab-form__label">
                  Search tenant (phone, ID, or name)
                  <input
                    className="ab-form__input"
                    type="text"
                    value={tenantSearchQuery}
                    onChange={(e) => {
                      const nextQuery = e.target.value;
                      setTenantSearchQuery(nextQuery);
                      setManualAssignModal((prev) => ({ ...prev, userId: '' }));
                    }}
                    placeholder="e.g. 0712..., ID number, Irene"
                    disabled={loading || actionRequestId === 'manual-bnb-assign'}
                  />
                </label>
                {tenantSearchLoading && (
                  <div className="ab-search-feedback">Searching tenants…</div>
                )}
                {tenantSearchError && (
                  <div className="ab-search-feedback ab-search-feedback--error">{tenantSearchError}</div>
                )}
                {!tenantSearchLoading && !tenantSearchError && tenantSearchQuery.trim() && !manualAssignModal.userId && (
                  <div className="ab-tenant-results">
                    {tenantSearchResults.length === 0 ? (
                      <div className="ab-search-feedback">No matching users found.</div>
                    ) : (
                      tenantSearchResults.map((tenant) => {
                        const fullName = [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ').trim() || 'Unnamed user';
                        const tenantUserId = tenant?.tenantUserId || '';
                        return (
                          <button
                            key={tenantUserId}
                            type="button"
                            className={`ab-tenant-result ${String(manualAssignModal.userId) === String(tenantUserId) ? 'active' : ''}`}
                            onClick={() => {
                              setManualAssignModal((prev) => ({
                                ...prev,
                                userId: String(tenantUserId || ''),
                              }));
                              setTenantSearchQuery(fullName);
                              setTenantSearchResults([]);
                              setTenantSearchError('');
                            }}
                          >
                            <span>{fullName}</span>
                            <span>{tenant?.tenantCode || 'No tenant code'}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}

                {manualAssignModal.userId && (
                  <div className="ab-form__hint">Tenant selected from search list.</div>
                )}

                <label className="ab-form__label">
                  Check-in date (Start date)
                  <input
                    className="ab-form__input"
                    type="date"
                    value={manualAssignModal.startDate}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, startDate: e.target.value }))}
                    disabled={loading || actionRequestId === 'manual-bnb-assign'}
                  />
                </label>

                <label className="ab-form__label">
                  Check-out date (End date)
                  <input
                    className="ab-form__input"
                    type="date"
                    value={manualAssignModal.endDate}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, endDate: e.target.value }))}
                    disabled={loading || actionRequestId === 'manual-bnb-assign'}
                  />
                </label>

                <label className="ab-form__label">
                  Amount paid (KES)
                  <input
                    className="ab-form__input"
                    type="number"
                    min="0"
                    placeholder="e.g. 30000"
                    value={manualAssignModal.amountPaid}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, amountPaid: e.target.value }))}
                    disabled={loading || actionRequestId === 'manual-bnb-assign'}
                  />
                </label>
              </div>

              <div className="ab-modal__actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={closeManualAssignModal}
                  disabled={loading || actionRequestId === 'manual-bnb-assign'}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                  onClick={submitManualAssign}
                  disabled={loading || actionRequestId === 'manual-bnb-assign'}
                >
                  {actionRequestId === 'manual-bnb-assign' ? 'Assigning…' : 'Assign tenant'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ═══ Booking info popup modal ═══ */}
      {evidenceModalBooking &&
        createPortal(
          <div className="ab-modal-overlay ab-modal-overlay--info" role="dialog" aria-modal="true" aria-label="Booking information" onClick={() => setEvidenceModalBooking(null)}>
            <div className="ab-modal ab-modal--info" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__title">Booking information</div>

              {evidenceModalError && (
                <div className="airbnb-bookings-view__banner airbnb-bookings-view__banner--error" role="alert" style={{ marginTop: 10 }}>
                  {evidenceModalError}
                </div>
              )}

              <div className="ab-info-modal__identity">
                <div>
                  <div className="airbnb-bookings-view__payments-label">Guest</div>
                  <div className="ab-info-modal__name">{evidenceModalBooking.guestName || 'Guest'}</div>
                  <div className="ab-info-modal__muted">{evidenceModalBooking.guestEmail || '—'}</div>
                </div>
                <span className={`airbnb-bookings-view__badge airbnb-bookings-view__badge--${(evidenceModalBooking.status || '').toLowerCase()}`}>
                  {evidenceModalBooking.status || '—'}
                </span>
              </div>

              <div className="ab-info-modal__quick-actions">
                <button type="button" className="ab-kyc-view-btn" onClick={() => openKycModal(evidenceModalBooking)}>
                  View KYC
                </button>
                <button type="button" className="ab-kyc-view-btn ab-score-view-btn" onClick={() => handleViewScore(evidenceModalBooking)}>
                  View Score
                </button>
              </div>

              <dl className="ab-info-modal__details">
                <div>
                  <dt>Booking code</dt>
                  <dd>{evidenceModalBooking.bookingCode || '—'}</dd>
                </div>
                <div>
                  <dt>Dates</dt>
                  <dd>{formatDateStr(evidenceModalBooking.checkInDate)} - {formatDateStr(evidenceModalBooking.checkOutDate)}</dd>
                </div>
                <div>
                  <dt>Nights</dt>
                  <dd>{evidenceModalBooking.nights || 0}</dd>
                </div>
                <div>
                  <dt>Total amount</dt>
                  <dd>{formatMoneyKES(evidenceModalBooking.totalAmount) || '—'}</dd>
                </div>
                <div>
                  <dt>Payment proof</dt>
                  <dd>{hasPaymentEvidence(evidenceModalBooking) ? (paymentsFullyConfirmed(evidenceModalBooking) ? 'Confirmed' : 'Submitted') : 'Waiting for proof'}</dd>
                </div>
                <div>
                  <dt>Total paid</dt>
                  <dd>{formatMoneyKES(evidenceModalBooking.depositPaidAmount || evidenceModalBooking.firstMonthPaidAmount) || '—'}</dd>
                </div>
              </dl>

              {(evidenceModalBooking.notes || evidenceModalBooking.rejectionReason || evidenceModalBooking.reason || evidenceModalBooking.cancelReason || evidenceModalBooking.rejection_reason) && (
                <div className="ab-info-modal__note">
                  <div className="airbnb-bookings-view__payments-label">
                    {String(evidenceModalBooking.status || '').toUpperCase() === 'REJECTED' ? 'Rejection' : 'Note'}
                  </div>
                  {evidenceModalBooking.notes || evidenceModalBooking.cancelReason || evidenceModalBooking.rejectionReason || evidenceModalBooking.reason || evidenceModalBooking.rejection_reason}
                </div>
              )}

              {!hasPaymentEvidence(evidenceModalBooking) && (isConfirmed(evidenceModalBooking.status) || isApproved(evidenceModalBooking.status)) && (
                <div className="airbnb-bookings-view__waiting-payment airbnb-bookings-view__waiting-payment--modal">
                  <div className="airbnb-bookings-view__waiting-payment-text">
                    <strong>Waiting for payment proof</strong>
                    <span>The guest has been notified to submit proof of payment.</span>
                  </div>
                </div>
              )}

              <div>
                {(evidenceModalBooking.depositEvidenceText || evidenceModalBooking.firstMonthEvidenceText || evidenceModalBooking.bookingFeeEvidenceText) && (
                  <div className="ab-info-modal__evidence-block">
                    <div className="airbnb-bookings-view__payments-label">M-Pesa Message</div>
                    <div className="airbnb-bookings-view__payments-note ab-info-modal__mpesa">
                      {evidenceModalBooking.depositEvidenceText || evidenceModalBooking.firstMonthEvidenceText || evidenceModalBooking.bookingFeeEvidenceText}
                    </div>
                  </div>
                )}

                {(evidenceModalBooking.depositEvidenceUrl || evidenceModalBooking.firstMonthEvidenceUrl || evidenceModalBooking.bookingFeeEvidencePhotoUrl) && (
                  <div className="ab-info-modal__evidence-block">
                    <div className="airbnb-bookings-view__payments-label">Payment photo</div>
                    <div className="airbnb-bookings-view__image-preview" onClick={(e) => openProtectedFile(e, evidenceModalBooking.depositEvidenceUrl || evidenceModalBooking.firstMonthEvidenceUrl || evidenceModalBooking.bookingFeeEvidencePhotoUrl, 'Receipt Photo')}>
                      <AuthenticatedImage src={evidenceModalBooking.depositEvidenceUrl || evidenceModalBooking.firstMonthEvidenceUrl || evidenceModalBooking.bookingFeeEvidencePhotoUrl} alt="Receipt Preview" />
                    </div>
                    <a
                      className="airbnb-bookings-view__payments-link"
                      href={evidenceModalBooking.depositEvidenceUrl || evidenceModalBooking.firstMonthEvidenceUrl || evidenceModalBooking.bookingFeeEvidencePhotoUrl}
                      onClick={(e) => openProtectedFile(e, evidenceModalBooking.depositEvidenceUrl || evidenceModalBooking.firstMonthEvidenceUrl || evidenceModalBooking.bookingFeeEvidencePhotoUrl, 'Receipt Photo')}
                    >
                      View uploaded photo
                    </a>
                  </div>
                )}
              </div>

              {/* Actions inside modal */}
              <div className="ab-modal__actions">
                {isRequestedOrPending(evidenceModalBooking.status) && (
                  <>
                    <button
                      type="button"
                      className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                      onClick={() => { setEvidenceModalBooking(null); handleApprove(evidenceModalBooking); }}
                      disabled={loading || actionRequestId === evidenceModalBooking.id}
                    >
                      {actionRequestId === evidenceModalBooking.id ? 'Working…' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      className="airbnb-bookings-view__button airbnb-bookings-view__button--cancel"
                      onClick={handleEvidenceReject}
                      disabled={loading || actionRequestId === evidenceModalBooking.id}
                    >
                      Reject
                    </button>
                  </>
                )}
                {(!isRequestedOrPending(evidenceModalBooking.status)) &&
                  hasPaymentEvidence(evidenceModalBooking) &&
                  !paymentsFullyConfirmed(evidenceModalBooking) &&
                  !['CONFIRMED', 'ASSIGNED', 'CHECKED_IN', 'CHECKED_OUT', 'MANUAL', 'REJECTED', 'CANCELLED'].includes(String(evidenceModalBooking.status || '').toUpperCase()) && (
                  <button
                    type="button"
                    className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                    onClick={() => { setEvidenceModalBooking(null); openConfirmPayments(evidenceModalBooking); }}
                    disabled={loading || actionRequestId === evidenceModalBooking.id}
                  >
                    Confirm payment
                  </button>
                )}
                {paymentsFullyConfirmed(evidenceModalBooking) && (() => {
                  const alreadyAssigned =
                    Boolean(evidenceModalBooking.assignedAt) ||
                    String(evidenceModalBooking.status || '').toUpperCase() === 'ASSIGNED' ||
                    String(evidenceModalBooking.status || '').toUpperCase() === 'CONFIRMED';
                  if (alreadyAssigned || ['REJECTED', 'CANCELLED', 'MANUAL'].includes(String(evidenceModalBooking.status || '').toUpperCase())) {
                    return null;
                  }
                  return (
                    <button
                      type="button"
                      className="airbnb-bookings-view__button airbnb-bookings-view__button--confirm"
                      onClick={() => { setEvidenceModalBooking(null); openAssignTenant(evidenceModalBooking); }}
                      disabled={loading || actionRequestId === evidenceModalBooking.id}
                    >
                      Assign guest to unit
                    </button>
                  );
                })()}
                {['CONFIRMED', 'ASSIGNED', 'CHECKED_IN', 'CHECKED_OUT', 'MANUAL'].includes(String(evidenceModalBooking.status || '').toUpperCase()) && (
                  <button
                    type="button"
                    className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                    onClick={() => openRatingModal(evidenceModalBooking)}
                  >
                    Rate Guest
                  </button>
                )}
                {(isConfirmed(evidenceModalBooking.status) || isApproved(evidenceModalBooking.status)) && !hasPaymentEvidence(evidenceModalBooking) && (
                  <button
                    type="button"
                    className="airbnb-bookings-view__button airbnb-bookings-view__button--cancel"
                    onClick={() => { setEvidenceModalBooking(null); handleCancel(evidenceModalBooking.id); }}
                    disabled={loading || actionRequestId === evidenceModalBooking.id}
                  >
                    {actionRequestId === evidenceModalBooking.id ? 'Working…' : 'Cancel booking'}
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ════════════════════════════════════════════ */}
      {/* ─── Reject booking modal ─────────────────── */}
      {/* ════════════════════════════════════════════ */}
      {rejectingReq &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="ab-modal-overlay" role="dialog" aria-modal="true" aria-label="Reject booking" onClick={cancelReject}>
            <div className="ab-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__title">Reject Booking</div>
              <div className="ab-modal__message">
                Please provide a reason for rejecting the booking for <strong>{rejectingReq.guestName || 'the guest'}</strong>.
              </div>

              <div className="ab-form">
                <label className="ab-form__label">
                  Reason for rejection
                  <textarea
                    className="airbnb-bookings-view__reject-textarea"
                    placeholder="e.g. Property unavailable for those dates"
                    value={rejectReasonById?.[rejectingReq.id] || ''}
                    onChange={(e) =>
                      setRejectReasonById((prev) => ({ ...prev, [rejectingReq.id]: e.target.value }))
                    }
                    disabled={loading || actionRequestId === rejectingReq.id}
                    style={{ marginTop: 6 }}
                  />
                </label>
                <div className="ab-form__hint">The guest will be notified of this reason.</div>
              </div>

              <div className="ab-modal__actions">
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={cancelReject}
                  disabled={loading || actionRequestId === rejectingReq.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--danger"
                  onClick={() => confirmReject(rejectingReq)}
                  disabled={loading || actionRequestId === rejectingReq.id || !rejectReasonById?.[rejectingReq.id]?.trim()}
                >
                  {actionRequestId === rejectingReq.id ? 'Working…' : 'Reject Booking'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ─── Tenant Score Modal ─────────────────────────────────────── */}
      {scoreModal.open &&
        scoreModal.req &&
        createPortal(
          <div className="ab-modal-overlay" onClick={closeScoreModal}>
            <div className="ab-modal tenant-score-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ab-modal__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h2 className="ab-modal__title">Tenant Score Details</h2>
                  <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                    {scoreModal.req.guestName || [scoreModal.req.requesterFirstName, scoreModal.req.requesterLastName].filter(Boolean).join(' ') || 'Guest'}
                  </p>
                </div>
                <button
                  type="button"
                  className="airbnb-bookings-view__button airbnb-bookings-view__button--ghost"
                  onClick={closeScoreModal}
                  style={{
                    flex: 'none',
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}
                >
                  ×
                </button>
              </div>

              <div className="tenant-score-modal__body" style={{ padding: '4px 0' }}>
                <div className="tenant-score-toolbar" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Period:</label>
                  <select
                    className="ab-form__input"
                    style={{ width: 'auto', padding: '6px 10px' }}
                    value={scoreModal.monthsBack}
                    onChange={(e) => handleViewScore(scoreModal.req, Number(e.target.value))}
                  >
                    <option value={3}>Last 3 months</option>
                    <option value={6}>Last 6 months</option>
                    <option value={12}>Last 12 months</option>
                    <option value={24}>Last 24 months</option>
                  </select>
                </div>

                 {scoreModal.loading ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div className="tenant-score-panel-loading">Loading tenant score...</div>
                  </div>
                ) : scoreModal.error ? (
                  <div style={{ padding: '24px' }}>
                    <p className="tenant-score-view-card__error">{scoreModal.error}</p>
                  </div>
                ) : scoreModal.score ? (
                  <>
                    <div className="tenant-score-hero tenant-score-hero--modal">
                      <div className="tenant-score-hero__value">{Number(scoreModal.score.tenantScore || 0).toFixed(1)}</div>
                      <div className="tenant-score-hero__band">{getScoreBand(scoreModal.score.tenantScore)}</div>
                      <div className="tenant-score-hero__period">{formatPeriod(scoreModal.score)}</div>
                    </div>

                    <div className="tenant-score-view-card__grid tenant-score-modal__grid">
                      <div>
                        <span className="kv-label">Months counted</span>
                        <span className="kv-value">{scoreModal.score.totalMonthsCounted ?? '—'}</span>
                      </div>
                      <div>
                        <span className="kv-label">On-time</span>
                        <span className="kv-value">{scoreModal.score.onTimeCount ?? 0}</span>
                      </div>
                      <div>
                        <span className="kv-label">Late</span>
                        <span className="kv-value">{scoreModal.score.lateCount ?? 0}</span>
                      </div>
                      <div>
                        <span className="kv-label">Missed</span>
                        <span className="kv-value">{scoreModal.score.missedCount ?? 0}</span>
                      </div>
                    </div>

                    {scoreModal.score.ratings && scoreModal.score.ratings.length > 0 && (
                      <div className="tenant-score-ratings-section">
                        <div className="ratings-section-header">
                          <h4 className="ratings-section-title">Recent Feedback</h4>
                          {scoreModal.score.ratings.length > 2 && (
                            <button 
                              type="button"
                              className="view-all-ratings-btn"
                              onClick={() => {
                                closeScoreModal();
                                if (onNavigate) {
                                  onNavigate('tenant-score', {
                                    ratingsTenantFilter: {
                                      userId: getGuestUserIdFromBooking(scoreModal.req),
                                      name: scoreModal.req.guestName || [scoreModal.req.requesterFirstName, scoreModal.req.requesterLastName].filter(Boolean).join(' '),
                                      ratings: scoreModal.score.ratings
                                    }
                                  });
                                }
                              }}
                            >
                              View all {scoreModal.score.ratings.length} ratings
                            </button>
                          )}
                        </div>
                        <div className="ratings-history-list">
                          {scoreModal.score.ratings.slice(0, 2).map((rating) => (
                            <div key={rating.id} className="rating-history-item">
                              <div className="rating-history-header">
                                <div className="rating-history-stars">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <FaStar
                                      key={star}
                                      className={`rating-star ${rating.score >= star ? 'active' : ''}`}
                                    />
                                  ))}
                                </div>
                                <span className="rating-history-date">
                                  {new Date(rating.createdAt).toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                              </div>
                              <p className="rating-history-message">"{rating.message}"</p>
                              <div className="rating-history-footer">
                                <span className="rating-history-property">{rating.propertyName}</span>
                                <span className="rating-history-actor">Rated by {rating.actorName}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ─── KYC Modal (same layout/CSS as Landlord PropertyUnitRequests) ─ */}
      {kycModal.open &&
        kycModal.req &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pur-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Tenant KYC Documents"
            onClick={closeKycModal}
          >
            <div className="pur-modal pur-modal--kyc" onClick={(e) => e.stopPropagation()}>
              <div className="pur-kyc-header">
                <div className="pur-kyc-header__info">
                  <div className="pur-modal__title">
                    {kycModal.req.guestName ||
                      [kycModal.req.requesterFirstName, kycModal.req.requesterLastName].filter(Boolean).join(' ') ||
                      'Guest'}
                  </div>
                  <div className="pur-kyc-meta">
                    {(kycModal.req.guestEmail || kycModal.req.requesterEmail) && (
                      <span className="pur-kyc-meta__email">
                        {kycModal.req.guestEmail || kycModal.req.requesterEmail}
                      </span>
                    )}
                    {kycModal.req.requesterTenantCode || kycModal.req.guestTenantCode ? (
                      <span className="pur-chip pur-chip--code">
                        {kycModal.req.requesterTenantCode || kycModal.req.guestTenantCode}
                      </span>
                    ) : null}
                    {(kycModal.req.requesterKycStatus || kycModal.req.guestKycStatus) && (
                      <span
                        className={`pur-chip pur-chip--kyc pur-chip--kyc-${String(
                          kycModal.req.requesterKycStatus || kycModal.req.guestKycStatus || ''
                        ).toLowerCase()}`}
                      >
                        KYC: {kycModal.req.requesterKycStatus || kycModal.req.guestKycStatus}
                      </span>
                    )}
                  </div>
                </div>
                <button type="button" className="pur-kyc-close" onClick={closeKycModal} aria-label="Close KYC modal">
                  ✕
                </button>
              </div>

              <div className="pur-kyc-images">
                {[
                  { label: 'ID Front', key: 'front' },
                  { label: 'ID Back', key: 'back' },
                  { label: 'Selfie', key: 'selfie' },
                ].map(({ label, key }) => {
                  const imgUrls = kycImageUrls[kycModal.req?.id];
                  const url = imgUrls?.[key];
                  const isLoadingThisReq = kycImagesLoading && !imgUrls;
                  const docUrl = activeKycDocUrls[key];
                  return (
                    <div key={key} className="pur-kyc-img-card">
                      <div className="pur-kyc-img-label">{label}</div>
                      {isLoadingThisReq ? (
                        <div className="pur-kyc-img-placeholder">
                          <div className="property-unit-requests__spinner" style={{ width: 28, height: 28, margin: 0 }} />
                        </div>
                      ) : url ? (
                        <img
                          src={url}
                          alt={label}
                          className="pur-kyc-img"
                          onClick={(e) => openProtectedFile(e, docUrl, label)}
                          title="Tap to open"
                        />
                      ) : (
                        <div className="pur-kyc-img-placeholder pur-kyc-img-placeholder--empty">
                          <span>Not available</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pur-modal__actions" style={{ justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={closeKycModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      <GuestChatModal
        isOpen={Boolean(chatBooking)}
        onClose={() => setChatBooking(null)}
        propertyId={getPropertyIdFromBooking(chatBooking)}
        guestUserId={getGuestUserIdFromBooking(chatBooking)}
        guestName={
          chatBooking?.guestName ||
          [chatBooking?.requesterFirstName, chatBooking?.requesterLastName].filter(Boolean).join(' ') ||
          'Guest'
        }
        propertySubtitle={chatBooking?.propertyName || propertyById[chatBooking?.propertyId] || ''}
        chatMode="bnb"
      />
      {/* ─── Tenant Rating Modal ─────────────────────────────────────── */}
      <TenantRatingModal
        isOpen={ratingModal.open}
        onClose={() => setRatingModal({ open: false, req: null, tenant: null })}
        tenant={ratingModal.tenant}
        propertyId={ratingModal.req?.propertyId}
        unitId={getUnitIdFromBooking(ratingModal.req)}
        onRatingSuccess={(msg) => {
          setRatingSuccessMessage(msg);
          setTimeout(() => setRatingSuccessMessage(''), 5000);
        }}
      />
      {/* Success Toast for Rating */}
      {ratingSuccessMessage && (
        <div className="pur-rating-toast" style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: '#16a34a',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          fontWeight: 600,
          animation: 'pur-toast-in 0.3s ease-out'
        }}>
          {ratingSuccessMessage}
        </div>
      )}
    </div>
  );
};

export default AirbnbBookingsView;
